import { useMemo } from "react"

import { useAgentsById } from "@/agents/catalog"

import { BrowseTable } from "@/components/tables/BrowseTable"
import type { RunsTableRowResource } from "@/types_api/backend/transport/runs_keyset_response"
import type { RunsTableResult } from "@/types/tables/RunsTable"

import { normalizeRunTableRow } from "../hooks/useRunsInfiniteQuery"
import { buildDisplayNameLookup } from "../utils/runsTableState"
import { buildRunColumns } from "./RunsTableColumns"

const baseLookups = {
  testSetNames: {},
  metricDisplayNames: {},
  metricRecordsById: {},
  personasById: {},
  getModelType: (value: string) =>
    value.replace("MODEL_TYPE_", "").replaceAll("_", " ").toLowerCase(),
  resolveCreator: (value: string | null | undefined) => value || "—",
}

/**
 * Multi-select list of simulated runs for picking dialogs. Every row shows its
 * checkbox and a click (shift or not) toggles one row; once `maxSelected` rows
 * are picked, the remaining rows are disabled until one is cleared.
 */
export function RunSelectionTable({
  items,
  selectedIds,
  onSelectionChange,
  maxSelected,
  isLoading,
  hasMore,
  isFetchingMore,
  onLoadMore,
}: {
  items: RunsTableRowResource[]
  selectedIds: Set<string>
  onSelectionChange: (ids: Set<string>) => void
  maxSelected: number
  isLoading: boolean
  hasMore: boolean
  isFetchingMore: boolean
  onLoadMore: () => void
}) {
  const { agentsById } = useAgentsById()
  const columns = useMemo(
    () =>
      buildRunColumns({
        columns: [
          { key: "status", label: "Status" },
          { key: "runName", label: "Name" },
          { key: "agentName", label: "Agent" },
          { key: "numSimulations", label: "Simulations" },
          { key: "created", label: "Created" },
          { key: "id", label: "ID" },
        ],
        tagsColumnWidth: 100,
        lookups: {
          ...baseLookups,
          agentNames: buildDisplayNameLookup(agentsById),
        },
      }).map((column) => ({ ...column, sortable: false })),
    [agentsById],
  )
  const rows = useMemo(() => {
    const seen = new Set<string>()
    return items.map(normalizeRunTableRow).filter((row) => {
      if (seen.has(row.id)) {
        return false
      }
      seen.add(row.id)
      return true
    })
  }, [items])
  const isAtLimit = selectedIds.size >= maxSelected

  const toggle = (row: RunsTableResult) => {
    const next = new Set(selectedIds)
    if (next.has(row.id)) {
      next.delete(row.id)
    } else if (!isAtLimit) {
      next.add(row.id)
    }
    onSelectionChange(next)
  }

  return (
    <BrowseTable
      displayControls={false}
      aria-label="Simulated runs table"
      data={rows}
      columns={columns}
      getRowId={(row) => row.id}
      onRowClick={toggle}
      selectable
      selectedIds={selectedIds}
      onSelectionChange={onSelectionChange}
      isRowSelectionDisabled={(row) => isAtLimit && !selectedIds.has(row.id)}
      showSelectAll={false}
      allowRangeSelection={false}
      selectionVisibility="always"
      rowClickSelects
      isLoading={isLoading}
      hasMore={hasMore}
      isFetchingMore={isFetchingMore}
      onLoadMore={onLoadMore}
      emptyState="No simulated runs found"
      className="h-full"
    />
  )
}
